// Schedule screen — ported from Claude Design/app/schedule.jsx
// Adapted to react-router + API publish queue.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import { VideoThumb, Icons, findTopic, ruPlural } from '../components/ui.jsx';

export function ScheduleScreen() {
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);
  const topics = useStore((s) => s.topics);

  const [tab, setTab] = useState('queue');
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());

  const apiStatus =
    tab === 'queue' ? 'scheduled' :
    tab === 'published' ? 'published' :
    tab === 'failed' ? 'failed' : 'all';

  const load = () => {
    api.publishQueue({ status: apiStatus })
      .then((r) => setItems(r.items || []))
      .catch((err) => toast(err.message, 'err'));
  };

  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const counts = {
    queue: items.filter((p) => p.status === 'scheduled' || p.status === 'review').length,
    published: tab === 'published' ? items.length : null,
    failed: items.filter((p) => p.status === 'failed').length,
    drafts: 0,
  };

  // Group items by day
  const grouped = groupByDay(items);

  return (
    <div className="screen">
      <div className="screen-header screen-header-compact">
        <h1 className="page-title">Расписание</h1>
        <span className="page-sub">следующая публикация через <b><Countdown items={items} /></b></span>
        <div className="grow" />
        <div className="screen-header-actions">
          <select className={'select ' + (tab === 'failed' && counts.failed > 0 ? 'select-warn' : '')}
                  value={tab} onChange={(e) => setTab(e.target.value)} title="фильтр по статусу">
            <option value="queue">в очереди · {counts.queue}</option>
            <option value="published">опубликовано{counts.published != null ? ` · ${counts.published}` : ''}</option>
            <option value="failed">ошибки · {counts.failed}</option>
          </select>
          <button className="btn btn-outline" onClick={() => navigate('/episodes')}>+ новый фрагмент</button>
        </div>
      </div>

      <div className="sched-grid">
        <aside className="sched-aside">
          <div className="ed-card">
            <MiniCalendar items={items} />
          </div>

          <div className="ed-card">
            <div className="ed-card-head">
              <div className="ed-card-title">Лучшее время</div>
            </div>
            {[
              ['08:00', 'утро · кофе', 0.8],
              ['13:30', 'обед',       0.7],
              ['19:00', 'вечер · пик', 1.0],
              ['21:30', 'перед сном', 0.85],
            ].map(([h, l, w]) => (
              <BestTime key={h} hour={h} label={l} weight={w} />
            ))}
            <div className="ed-card-hint">посчитано по аудитории · dosmundos.pe</div>
          </div>
        </aside>

        <div className="sched-main">
          {items.length === 0
            ? <EmptyState tab={tab} />
            : grouped.map((d) => <DayGroup key={d.key} d={d} topics={topics} navigate={navigate} reload={load} />)}
        </div>
      </div>
    </div>
  );
}

function groupByDay(items) {
  const out = new Map();
  const todayKey = new Date().toDateString();
  const tomorrowKey = new Date(Date.now() + 24 * 3600e3).toDateString();
  for (const item of items) {
    const d = new Date(item.scheduled_at);
    const k = d.toDateString();
    let label;
    if (k === todayKey) label = 'СЕГОДНЯ';
    else if (k === tomorrowKey) label = 'ЗАВТРА';
    else label = d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();
    if (!out.has(k)) out.set(k, { key: k, label, date: d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }), posts: [] });
    out.get(k).posts.push(item);
  }
  return Array.from(out.values()).sort((a, b) => new Date(a.posts[0].scheduled_at) - new Date(b.posts[0].scheduled_at));
}

function Countdown({ items }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const upcoming = items
    .filter((p) => p.status === 'scheduled')
    .map((p) => new Date(p.scheduled_at).getTime())
    .filter((t) => t > now)
    .sort((a, b) => a - b);
  if (!upcoming.length) return <span style={{ fontVariantNumeric: 'tabular-nums' }}>—</span>;
  let diff = Math.max(0, upcoming[0] - now);
  const h = Math.floor(diff / 3600e3); diff %= 3600e3;
  const m = Math.floor(diff / 60e3);   diff %= 60e3;
  const s = Math.floor(diff / 1e3);
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{h}ч {String(m).padStart(2, '0')}м {String(s).padStart(2, '0')}с</span>;
}

function MiniCalendar({ items }) {
  const today = new Date();
  const monthName = today.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1).getDay() || 7; // ISO: Mon=1..Sun=7
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayDate = today.getDate();

  const scheduled = new Set();
  const published = new Set();
  const failed = new Set();
  for (const item of items) {
    const d = new Date(item.scheduled_at);
    if (d.getMonth() !== month || d.getFullYear() !== year) continue;
    const day = d.getDate();
    if (item.status === 'failed') failed.add(day);
    else if (item.status === 'scheduled') scheduled.add(day);
    else if (item.status === 'published') published.add(day);
  }

  const cells = [];
  for (let i = 1; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <>
      <div className="mini-cal-head">
        <button className="iconbtn">‹</button>
        <div className="ed-card-title capitalize">{monthName}</div>
        <button className="iconbtn">›</button>
      </div>
      <div className="mini-cal-dow">
        {['пн','вт','ср','чт','пт','сб','вс'].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="mini-cal-grid">
        {cells.map((d, i) => {
          if (d == null) return <div key={i} />;
          const isToday = d === todayDate;
          const sched = scheduled.has(d);
          const pub = published.has(d);
          const fail = failed.has(d);
          return (
            <button key={i} className={'mini-cal-day' + (isToday ? ' day-today' : '') + (sched ? ' day-sched' : '')}>
              <span>{d}</span>
              <div className="day-dots">
                {fail && <span className="dot dot-red" />}
                {sched && !fail && <span className="dot dot-dark" />}
                {pub && <span className={'dot ' + (isToday ? 'dot-light' : 'dot-mute')} />}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mini-cal-legend">
        <span><span className="dot dot-dark" />в очереди</span>
        <span><span className="dot dot-mute" />опубл.</span>
        <span><span className="dot dot-red" />ошибка</span>
      </div>
    </>
  );
}

function BestTime({ hour, label, weight }) {
  return (
    <div className="bt-row">
      <span className="bt-hour">{hour}</span>
      <div className="bt-bar"><span style={{ width: (weight * 100) + '%' }} /></div>
      <span className="bt-label">{label}</span>
    </div>
  );
}

function DayGroup({ d, topics, navigate, reload }) {
  return (
    <div className="day-group">
      <div className="day-head">
        <span className={'day-label ' + (d.label === 'СЕГОДНЯ' ? 'day-label-today' : '')}>{d.label}</span>
        {d.date && <span className="muted small">{d.date}</span>}
        <div className="hrule" />
        <span className="muted small">{d.posts.length} {ruPlural(d.posts.length, 'пост', 'поста', 'постов')}</span>
      </div>
      <div className="day-posts">
        {d.posts.map((p) => <SchedCard key={p.id} p={p} topics={topics} navigate={navigate} reload={reload} />)}
      </div>
    </div>
  );
}

function SchedCard({ p, topics, navigate, reload }) {
  const isFailed = p.status === 'failed';
  const isReview = p.status === 'review';
  const time = new Date(p.scheduled_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  const countdown = (() => {
    const ms = new Date(p.scheduled_at).getTime() - Date.now();
    if (p.status !== 'scheduled') return null;
    if (ms <= 0) return 'уже сейчас';
    const h = Math.floor(ms / 3600e3);
    const m = Math.floor((ms % 3600e3) / 60e3);
    return h > 0 ? `через ${h}ч ${m}м` : `через ${m}м`;
  })();

  return (
    <div className={'sched-card ' + (isFailed ? 'sched-failed' : isReview ? 'sched-review' : '')}
         onClick={() => navigate(`/clip/${p.clip_id}`)}>
      <div className="sched-time">
        <div className="sched-time-text">{time}</div>
        {countdown && <div className="sched-countdown">{countdown}</div>}
      </div>
      <div className="sched-thumb">
        <VideoThumb aspect="9/16" />
      </div>
      <div className="sched-body">
        <div className="sched-title-es">{p.clip_title || '—'}</div>
        {p.clip_title_ru && <div className="sched-title-ru">{p.clip_title_ru}</div>}
        <div className="sched-meta">
          {(p.targets || []).map((t) => (
            <span key={t} className="chip chip-soft small">{t.replace('_', ' ')}</span>
          ))}
        </div>
        {p.caption && <div className="sched-caption">{p.caption.slice(0, 160)}{p.caption.length > 160 ? '…' : ''}</div>}
        {isFailed && <div className="sched-error">⚠ {p.error_message || 'ошибка публикации'}</div>}
        {isReview && <div className="sched-info">ⓘ нужно ревью — подтверди публикацию</div>}
      </div>
      <div className="sched-actions" onClick={(e) => e.stopPropagation()}>
        {isFailed ? (
          <>
            <button className="btn btn-publish small" onClick={() => api.retryPublish(p.id).then(reload)}>повторить</button>
            <button className="btn btn-ghost small" onClick={() => api.cancelPublish(p.id).then(reload)}>удалить</button>
          </>
        ) : (
          <>
            <button className="btn btn-outline small" onClick={() => navigate(`/clip/${p.clip_id}`)}>править</button>
            <button className="btn btn-ghost small" onClick={() => api.cancelPublish(p.id).then(reload)}>отменить</button>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ tab }) {
  const labels = {
    queue:     ['пока пусто', 'нажми «опубликовать» в редакторе чтобы поставить пост в очередь'],
    published: ['нет публикаций', 'история публикаций будет здесь после первого поста'],
    failed:    ['всё хорошо', 'нет постов с ошибками'],
  };
  const [a, b] = labels[tab] || ['—', '—'];
  return (
    <div className="empty-state">
      <div className="empty-icon">◇</div>
      <div className="empty-title">{a}</div>
      <div className="empty-sub">{b}</div>
    </div>
  );
}
